import { AfterViewInit, Component, ContentChild, ElementRef, EventEmitter, HostBinding, HostListener, Injector, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { AbstractControl, ControlValueAccessor, NgControl, ValidationErrors, Validator, Validators } from "@angular/forms";
import { ComponentUtility } from "@app/class/ComponentUtility";
import { FileReaderService } from "@services/fileReader.service";
import { HTMLService } from "@services/htmlService.service";
import { TranslationService } from "@services/translation.service";
import "./fileUpload.scss";

export interface IFileUploadDetails {
    file: File;
    fileString: string;
}

@Component({
    selector: 'file-upload',
    template: require('./fileUpload.html'),
    providers: [
        ComponentUtility.createValueAccessorProvider(FileUploadComponent),
        ComponentUtility.createValidatorProvider(FileUploadComponent)
    ]
})
export class FileUploadComponent implements OnInit, OnChanges, AfterViewInit, ControlValueAccessor, Validator {
    @Input() file: File;
    @Input() fileString: string;
    @Input() isImage: boolean;
    @Input() uploadMessage: string;
    @Input() showClear: boolean;
    @Input() showChange: boolean;
    @Input() whiteBackground: boolean;
    @Input() maxFileSizeMb: number;

    @Output() onFileChanged: EventEmitter<IFileUploadDetails> = new EventEmitter();
    @Output() onFileCleared: EventEmitter<any> = new EventEmitter();

    @ViewChild("fileInput", { static: false }) fileInput: ElementRef;

    @HostListener("drop", ["$event"]) onDropFile(event: DragEvent): void {
        event.preventDefault();
        this.processNewFile(event.dataTransfer.files[0]);
    }

    @HostListener('dragover', ['$event']) onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.setDraggingState(true);
    }

    @HostListener('dragleave', ['$event']) onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.setDraggingState(false);
    }

    @HostBinding("class.required") @Input() required = false;

    value: any;
    inputId: string;
    showMessage: boolean;

    hasFile: boolean;
    hasProjectedContent: boolean;
    hasFileChanged: boolean;
    fileSizeLabel: string;
    dragging: boolean;

    fileTooLarge = false;
    maxFileSize = 5 * 1024 * 1024; // 5mb
    maxFileSizeLabel: string;

    constructor(
        protected readonly injector: Injector,
        private readonly elementRef: ElementRef,
        private readonly fileReaderService: FileReaderService,
        private readonly htmlService: HTMLService
    ) {
    }

    ngOnInit() {
        if (!this.file) {
            this.file = null;
        }

        this.inputId = `fileinputid-${Math.floor(Math.random() * 1000)}`;
        this.maxFileSizeLabel = this.fileReaderService.fileSizeAsString(this.maxFileSize);
    }

    ngAfterViewInit() {
        var ngContent = this.htmlService.findNearest(this.elementRef.nativeElement, ".ng-content")
        this.hasProjectedContent = ngContent?.childElementCount > 0;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['file'] && this.file) {
            this.onFileUpdated();
        }

        if (changes["fileString"]) {
            this.onFileStringUpdated();
        }

        if (changes['maxFileSizeMb']) {
            this.maxFileSize = this.maxFileSizeMb * 1024 * 1024;
        }
    }

    onInputFileChanged(event: Event) {
        this.file = (event.target as HTMLInputElement).files[0];

        this.processNewFile(this.file);
    }

    changeFile(event: Event): void {
        event.preventDefault();
        this.fileInput.nativeElement.click();
    }

    clearFile() {
        this.fileInput.nativeElement.value = "";
        this.file = null;
        this.fileString = null;
        this.showMessage = true;
        this.setDraggingState(false);

        this.updateHasFile();

        if (this.onFileCleared) {
            this.onFileCleared.emit();
        }
    }

    // --- Control Value Accessor

    onChange: (value: any) => void;
    onTouched: () => void;
    control: NgControl;

    registerOnChange(callback: (value: any) => void): void {
        this.onChange = callback;
    }

    registerOnTouched(callback: () => void): void {
        this.onTouched = callback;
    }

    writeValue(value: any): void {
        this.value = value;
    }

    markAsTouched(): void {
        if (!this.onTouched || !this.control || this.control.touched) {
            return;
        }

        this.onTouched();
    }

    // Validator

    validate(control: AbstractControl): ValidationErrors | null {
        this.control = this.injector.get(NgControl);

        this.required = control.hasValidator(Validators.required);

        return null;
    }
    // -----------------------------

    private processNewFile(file: File): void {
        if (!file) {
            this.clearFile();
            return;
        }

        this.file = file;
        this.hasFileChanged = true;
        this.fileTooLarge = false;
        this.setDraggingState(false);

        this.onFileUpdated().then(() => {
            this.onFileChanged.emit({ file: this.file, fileString: this.fileString });
        });
    }

    private setDraggingState(state: boolean): void {
        this.dragging = state;
    }

    private onFileStringUpdated() {
        this.updateHasFile();

        if (!this.fileString) {
            return;
        }

        this.showMessage = false;
    }

    private onFileUpdated(): Promise<any> {
        return new Promise((resolve, reject) => {

            this.updateHasFile();

            if (!this.file) {
                reject("No file provided");
            }

            if (this.file.size > this.maxFileSize) {
                this.clearFile();
                this.fileTooLarge = true;

                reject("File too large");
            }

            this.fileSizeLabel = this.fileReaderService.fileSizeAsString(this.file.size);

            this.fileReaderService
                .readAsDataUrl(this.file)
                .then((result) => {
                    this.fileString = "";

                    // Don't create fileString if its not an image
                    if (this.fileReaderService.fileIsImage(this.file)) {
                        this.fileString = result;
                    }

                    this.updateHasFile();

                    resolve("File updated");
                }).catch((error: string) => {
                    reject(`Error updating file ${error}`);
                });
        });
    }

    private updateHasFile() {
        if (this.isImage) {
            this.hasFile = !!this.fileString;
        }
        else {
            this.hasFile = !!this.file;
        }
    }
}
